// Button: primary (the one action), secondary (the default), ghost (quiet), danger (destructive);
// sizes md, sm, lg and icon (square: give it an aria-label). `buttonClass` styles a link the same
// way (<a>, <Link>). Ported from the Humint Assistant's components/ui/button.tsx, on Shipyard's tokens.
import type { ComponentProps } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'sm' | 'lg' | 'icon';

const BASE =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded border font-medium cursor-pointer no-underline hover:no-underline' +
  ' disabled:cursor-not-allowed disabled:bg-surface disabled:text-ink-muted disabled:border-hairline';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-action-bg text-action-ink border-action-bg',
  secondary: 'bg-page text-ink-display border-hairline hover:bg-surface hover:border-hairline-strong',
  ghost: 'bg-transparent text-ink-muted border-transparent hover:bg-surface hover:text-ink-display',
  danger: 'bg-page text-ink-display border-danger hover:bg-surface',
};

const SIZE: Record<ButtonSize, string> = {
  md: 'h-[30px] px-2.5 text-[12.5px] coarse:min-h-10',
  sm: 'h-[26px] px-2 text-[12px] coarse:min-h-10',
  lg: 'h-11 px-[18px] text-[15px] font-semibold',
  icon: 'h-[30px] w-[30px] justify-center p-0 text-[12.5px] coarse:min-h-10 coarse:w-10',
};

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string): string {
  return cn(BASE, VARIANT[variant], SIZE[size], className);
}

export interface ButtonProps extends ComponentProps<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function Button({ variant, size, className, type = 'button', ...props }: ButtonProps) {
  return <button type={type} className={buttonClass(variant, size, className)} {...props} />;
}
